import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Chore, ChoreInstance, Member } from '@pacer/shared';
import * as api from '../api';
import { CheckToggle } from '../components/CheckToggle';
import { Modal } from '../components/Modal';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
  cx,
} from '../components/ui';
import { choreSortKey, choreUrgency, fmtDueTime } from '../lib/chores';
import { fmtDayLabel, useZone } from '../lib/dates';
import { useActiveMemberId } from '../state/activeMember';
import { useNow } from '../state/useNow';
import { queryKeys } from '../state/queries';
import { confirmDelete } from '../state/ui';

const URGENCY_CLASS = { overdue: 'text-danger', soon: 'text-warning', ok: 'text-muted' } as const;

const REPEATS = [
  { label: 'Anytime (no schedule)', value: '' },
  { label: 'Every day', value: 'FREQ=DAILY' },
  { label: 'Weekdays', value: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR' },
  { label: 'Every week', value: 'FREQ=WEEKLY' },
  { label: 'Every 2 weeks', value: 'FREQ=WEEKLY;INTERVAL=2' },
  { label: 'Every month', value: 'FREQ=MONTHLY' },
];

const DAYS_SHOWN = 7;

export function Chores() {
  const zone = useZone();
  const [editing, setEditing] = useState<Chore | 'new' | null>(null);

  const today = DateTime.now().setZone(zone).startOf('day');
  const from = today.toISODate()!;
  const to = today.plus({ days: DAYS_SHOWN }).toISODate()!;

  const { data: instances = [], isLoading } = useQuery({
    queryKey: [...queryKeys.chores, from, to],
    queryFn: () => api.getChores(from, to),
  });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: scores = [] } = useQuery({ queryKey: ['scores'], queryFn: () => api.getScores(30) });

  const byDay = useMemo(() => {
    const map = new Map<string, ChoreInstance[]>();
    for (const i of instances) map.set(i.dueOn, [...(map.get(i.dueOn) ?? []), i]);
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([day, items]) =>
          [
            day,
            [...items].sort(
              (x, y) =>
                choreSortKey(x.chore.dueTime).localeCompare(choreSortKey(y.chore.dueTime)) ||
                x.chore.title.localeCompare(y.chore.title),
            ),
          ] as [string, ChoreInstance[]],
      );
  }, [instances]);

  if (isLoading) return <Spinner label="loading" />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10">
      <PageHeader
        eyebrow="Who does what"
        title="Chores"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New chore
          </Button>
        }
      />

      {members.length > 0 && scores.some((s) => s.completed > 0) && (
        <Card className="mb-6 flex flex-wrap items-center gap-5 px-4 py-3">
          <span className="eyebrow">Last 30 days</span>
          {members.map((m) => {
            const s = scores.find((x) => x.memberId === m.id);
            if (!s || s.completed === 0) return null;
            return (
              <span key={m.id} className="flex items-center gap-2">
                <Avatar name={m.name} color={m.color} photoUrl={m.avatarUrl} size={24} />
                <span className="tabular font-mono text-[12px] text-secondary">
                  {s.completed} done
                  {s.points > 0 && <span className="text-accent"> · {s.points} pts</span>}
                </span>
              </span>
            );
          })}
        </Card>
      )}

      {byDay.length === 0 ? (
        <EmptyState
          title="No chores yet"
          hint="Add a chore and give it a schedule — it'll show up here and on the wall."
          action={
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              New chore
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {byDay.map(([day, items]) => (
            <section key={day} className="flex flex-col gap-2">
              <h2 className="eyebrow">{fmtDayLabel(DateTime.fromISO(day, { zone }), zone)}</h2>
              <Card className="overflow-hidden">
                <ul>
                  {items.map((i) => (
                    <ChoreRow
                      key={`${i.chore.id}@${i.dueOn}`}
                      instance={i}
                      members={members}
                      onEdit={() => setEditing(i.chore)}
                    />
                  ))}
                </ul>
              </Card>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <ChoreEditor
          chore={editing === 'new' ? null : editing}
          members={members}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ChoreRow({
  instance,
  members,
  onEdit,
}: {
  instance: ChoreInstance;
  members: Member[];
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const zone = useZone();
  const now = useNow(60_000);
  const activeId = useActiveMemberId();
  const assignee = members.find((m) => m.id === instance.chore.assignedMemberId);
  const doneBy = members.find((m) => m.id === instance.doneBy);
  const { dueTime } = instance.chore;
  const urgency = choreUrgency(instance.dueOn, dueTime, zone, DateTime.fromJSDate(now).setZone(zone));

  const toggle = useMutation({
    mutationFn: () =>
      api.completeChore(instance.chore.id, instance.dueOn, !instance.done, activeId ?? instance.chore.assignedMemberId),
    // Optimistic across every chores window (this view's range + the dashboard's today).
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: queryKeys.chores });
      const prev = qc.getQueriesData<ChoreInstance[]>({ queryKey: queryKeys.chores });
      qc.setQueriesData<ChoreInstance[]>({ queryKey: queryKeys.chores }, (old) =>
        old?.map((i) =>
          i.chore.id === instance.chore.id && i.dueOn === instance.dueOn
            ? { ...i, done: !i.done, doneBy: !i.done ? (activeId ?? i.chore.assignedMemberId) : null }
            : i,
        ),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.chores });
      await qc.invalidateQueries({ queryKey: ['scores'] });
    },
  });

  return (
    <li className="group flex items-center gap-3 border-b border-border-subtle px-3 transition-colors last:border-b-0 hover:bg-surface-high/40">
      <button
        onClick={() => toggle.mutate()}
        className="touch flex flex-1 items-center gap-3 py-1 text-left"
        aria-pressed={instance.done}
      >
        <CheckToggle checked={instance.done} onColor="success" color={assignee?.color ?? '#4caf7d'} />

        <span className="min-w-0 flex-1">
          <span
            className={cx(
              'block truncate text-[15px] transition-colors',
              instance.done ? 'text-muted line-through' : 'text-primary',
            )}
          >
            {instance.chore.title}
          </span>
          {instance.done && doneBy && (
            <span className="font-mono text-[12px] text-muted">Done by {doneBy.name}</span>
          )}
        </span>
      </button>

      {dueTime && !instance.done && (
        <span className={cx('shrink-0 font-mono text-[12px]', URGENCY_CLASS[urgency])}>
          {urgency === 'overdue' ? `overdue · ${fmtDueTime(dueTime)}` : `by ${fmtDueTime(dueTime)}`}
        </span>
      )}
      {instance.chore.points > 0 && <Badge tone="accent">{instance.chore.points} pts</Badge>}
      {assignee && <Avatar name={assignee.name} color={assignee.color} photoUrl={assignee.avatarUrl} size={26} />}

      <IconButton
        icon="settings"
        label={`Edit ${instance.chore.title}`}
        size="sm"
        onClick={onEdit}
        className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
      />
    </li>
  );
}

function ChoreEditor({
  chore,
  members,
  onClose,
}: {
  chore: Chore | null;
  members: Member[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(chore?.title ?? '');
  const [assignedMemberId, setAssigned] = useState(chore?.assignedMemberId ?? '');
  const [rrule, setRrule] = useState(chore?.rrule ?? '');
  const [points, setPoints] = useState(String(chore?.points ?? 0));
  const [dueTime, setDueTime] = useState(chore?.dueTime ?? '');
  const [reminderMinutes, setReminderMinutes] = useState(
    chore?.reminderMinutes != null ? String(chore.reminderMinutes) : '',
  );
  const [error, setError] = useState<string | null>(null);

  const done = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.chores });
    onClose();
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: title.trim(),
        assignedMemberId: assignedMemberId || null,
        rrule: rrule || null,
        points: Number.parseInt(points, 10) || 0,
        dueTime: dueTime || null,
        reminderMinutes: dueTime && reminderMinutes !== '' ? Number(reminderMinutes) : null,
      };
      return chore ? api.updateChore(chore.id, payload) : api.createChore(payload);
    },
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteChore(chore!.id),
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={chore ? 'Edit' : 'New'}
      title={chore ? 'Edit chore' : 'New chore'}
      footer={
        <>
          {chore && (
            <Button
              variant="danger"
              onClick={async () => {
                if (await confirmDelete(`the “${chore.title}” chore`)) remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Delete
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!title.trim() || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Chore">
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Take the bins out"
            enterKeyHint="done"
          />
        </Field>

        <Field label="Who">
          <Select value={assignedMemberId} onChange={(e) => setAssigned(e.target.value)}>
            <option value="">Anyone</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Repeats">
          <Select value={rrule} onChange={(e) => setRrule(e.target.value)}>
            {REPEATS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
            {rrule && !REPEATS.some((r) => r.value === rrule) && (
              <option value={rrule}>Custom ({rrule})</option>
            )}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="By (time)" hint="Optional deadline.">
            <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} />
          </Field>
          <Field label="Remind">
            <Select
              value={reminderMinutes}
              onChange={(e) => setReminderMinutes(e.target.value)}
              disabled={!dueTime}
            >
              <option value="">No reminder</option>
              <option value="0">At the time</option>
              <option value="30">30 min before</option>
              <option value="60">1 hour before</option>
              <option value="120">2 hours before</option>
            </Select>
          </Field>
        </div>

        <Field label="Points" hint="Optional — for keeping score. 0 to ignore.">
          <Input
            type="number"
            min={0}
            max={999}
            inputMode="numeric"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
          />
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
